import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import MapIcon from './MapIcon';
import { PENETRATION_LIMIT } from '../engine/battlefield/lineOfSight';
import { formatMetres } from '../engine/battlefield/measure';

const COVER_LABELS = {
  none: 'no cover',
  low: 'low cover',
  half: 'half cover',
  threeQuarter: '3/4 cover',
  full: 'full cover',
};

const BAND_LABELS = {
  pointBlank: 'Point Blank (+30)',
  short: 'Short (+10)',
  normal: 'Normal (0)',
  long: 'Long (-10)',
  extreme: 'Extreme (-30)',
};

/**
 * Which cover won, and how many it beat. Cover is NOT cumulative: the
 * highest thing in the way applies and the rest are ignored. That was
 * invisible until the readout said which one won.
 */
function CoverReadout({ cover, coverSource, coverCount, beyond = [] }) {
  const several = coverCount > 1;
  const farNote = farCoverNote(cover, beyond);
  return (
    <span data-testid="readout-cover" title={coverTitle(several, coverCount, beyond)}>
      {COVER_LABELS[cover.level]}
      {cover.level !== 'none' ? ` (AP ${cover.ap})` : ''}
      {coverSource ? ` - ${coverSource}` : ''}
      {several ? ` (highest of ${coverCount})` : ''}
      {farNote ? <span data-testid="readout-cover-far">{farNote}</span> : null}
    </span>
  );
}

/**
 * COVER NEAR THE TARGET (2026-10-25): when the only thing in the way stands too far from the target to count,
 * say so - "the berm is 4.6 m from him" - rather than leaving a bare "no cover" beside a berm the tape crosses.
 */
function farCoverNote(cover, beyond) {
  if (cover.level !== 'none' || !beyond.length) return null;
  const nearest = beyond[0];
  return ` - ${nearest.source ? `the ${nearest.source}` : 'the terrain'} is ${formatMetres(nearest.metres)} from him`;
}

function coverTitle(several, coverCount, beyond) {
  const lines = [];
  if (several)
    lines.push(
      `The line crosses ${coverCount} things that grant cover. The HIGHEST applies - cover is never added up.`,
    );
  if (beyond.length)
    lines.push('Cover counts only near the target: farther terrain still blocks sight, but covers nobody.');
  return lines.length ? lines.join(' ') : undefined;
}

/**
 * Two different reasons a shot is impossible, worth telling apart: you
 * cannot SEE, or you cannot get THROUGH. A glass tower is the second.
 */
function BlockedNote({ resistance }) {
  const tooThick = resistance >= PENETRATION_LIMIT;
  return (
    <span
      className={styles.readoutBlocked}
      data-testid="readout-blocked"
      title={
        tooThick
          ? `${Math.round(resistance)} hardness-metres of material - too much to shoot through.`
          : 'Something opaque stands in the way.'
      }
    >
      ✘ {tooThick ? 'too much material' : 'line of sight blocked'}
    </span>
  );
}

/**
 * A BUTTON, matching ⊘ Terrain beside it. It was a checkbox with a long
 * label in a bar that wraps, which made it a small moving target.
 */
function TargetTerrainButton({ on, onToggle }) {
  return (
    <button
      type="button"
      aria-pressed={Boolean(on)}
      aria-label="This combatant's sight and cover ignore terrain"
      data-testid="readout-ignore-terrain"
      className={clsx(styles.mode, on && styles.modeActive)}
      onClick={() => onToggle(!on)}
      title="One-off ruling: line of sight to and from this combatant ignores terrain, and terrain grants them no cover. Movement is NOT affected - that is the Terrain toggle and the movement traits"
    >
      <MapIcon name="flying" /> Ignores LoS
    </button>
  );
}

/** Release a pinned cover, or pin this one. */
function PinButton({ pinned, canApply, applied, targetName, onUnpin, onApply }) {
  if (pinned) {
    return (
      <button
        type="button"
        className={common.btnSmall}
        data-testid="battlefield-unpin"
        onClick={onUnpin}
        title={`${targetName || 'This combatant'} is carrying a cover you set by hand. Clear it and let the map answer again.`}
      >
        📌 Pinned - release
      </button>
    );
  }
  if (!canApply) return null;
  return (
    <button
      type="button"
      className={common.btnSmall}
      data-testid="battlefield-apply"
      onClick={onApply}
      title={`Hold this cover and range band on ${targetName || 'the target'} until you clear it, whatever the map says next`}
    >
      {applied ? '✔ Pinned' : '📌 Pin to target'}
    </button>
  );
}

/**
 * BattlefieldReadout - what the tape measure found, and the one button
 * that puts it to work.
 *
 * Two things can be on show. A TAPE is a question - "what if I shot from
 * here?" - and answering it must not silently change anything. The LIVE
 * line is the actor→target one the map derives on its own, which is what
 * the attack is already rolling against.
 *
 * So the button means PIN, not apply: it writes the value as a manual
 * ruling, which outranks the map and holds until somebody clears it. That
 * is the escape hatch for a shot the geometry gets wrong.
 */
export default function BattlefieldReadout(props) {
  const {
    measurement,
    live = false,
    pinned = false,
    ignoringTerrain,
    targetIgnoresTerrain,
    onToggleTargetTerrain,
  } = props;
  if (!measurement) {
    return (
      <div className={styles.readoutBar} data-testid="battlefield-readout">
        <span className={styles.readoutHint}>Drag between two points. Markers snap. Esc dismisses.</span>
      </div>
    );
  }
  const { metres, rangeBand, blocked } = measurement;
  return (
    <div className={styles.readoutBar} data-testid="battlefield-readout">
      <span className={styles.readoutValue} data-testid="readout-distance">
        {formatMetres(metres)}
      </span>
      {rangeBand ? (
        <span data-testid="readout-band">{BAND_LABELS[rangeBand] || rangeBand}</span>
      ) : (
        <span className={styles.readoutHint}>out of range</span>
      )}
      <CoverReadout {...measurement} />
      {blocked && <BlockedNote resistance={measurement.resistance} />}
      {ignoringTerrain && (
        <span className={styles.readoutHint} data-testid="readout-ignoring">
          ⊘ terrain ignored
        </span>
      )}
      <div className={styles.spacer} />
      {onToggleTargetTerrain && <TargetTerrainButton on={targetIgnoresTerrain} onToggle={onToggleTargetTerrain} />}
      {live && !pinned && (
        <span className={styles.readoutHint} data-testid="battlefield-live-note">
          live - follows them about
        </span>
      )}
      <PinButton {...props} pinned={pinned} />
    </div>
  );
}
